import { Suspense, useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import {
    faArrowUpRightFromSquare,
    faBan,
    faCalendarCirclePlus,
    faCalendarXmark,
    faClone,
    faEye,
    faEyeSlash,
    faFilePen,
    faPause,
    faPenToSquare,
    faPlay,
    faQrcode,
    faTrashXmark,
    faTrophy,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    pauseCampaignItemBidding,
    resumeCampaignItemBidding,
    updateCampaignItem,
} from 'api/campaigns';
import * as DropdownMenu from 'components/dropdown-menu';
import * as CancelAuctionItem from 'components/pages/campaign-settings/campaign-auction/item-details/CancelAuctionItem';
import * as DeleteItem from 'components/pages/campaign-settings/campaign-auction/item-details/DeleteItem';
import * as EndListingItem from 'components/pages/campaign-settings/campaign-auction/item-details/EndListingItem';
import * as ExportItemQRCode from 'components/pages/campaign-settings/campaign-auction/item-details/ExportItemQRCode';
import * as ToggleHideListingItem from 'components/pages/campaign-settings/campaign-auction/item-details/ToggleHideListingItem';
import * as ExtendItemDialog from 'components/pages/campaign-settings/campaign-auction/items-form/ExtendItemDialog';
import { ItemOptionsContext } from 'components/pages/campaign-settings/campaign-auction/items-form/ItemOptionsContext';
import { useCampaignItemRoutes } from 'components/pages/campaign-settings/campaign-auction/useCampaignItemRoutes';
import { didItemEnd } from 'components/pages/campaign-settings/campaign-auction/util';
import { UnstyledAnchor } from 'components/unstyled-link';
import { CampaignItemRouteParams } from 'customHooks/useCurrentCampaignItem';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { twJoin } from 'tailwind-merge';
import { AuctionItem, Campaign } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ItemOptionsProps extends DropdownMenu.DropdownMenuProps {
    item: AuctionItem;
    campaign: Campaign;
}

interface ItemOptionsEditProps extends Omit<DropdownMenu.DropdownMenuLinkItemProps, 'to'> {}
interface ItemOptionsDetailsProps extends Omit<DropdownMenu.DropdownMenuLinkItemProps, 'to'> {}
interface ItemOptionsDeclareWinnerProps
    extends Omit<DropdownMenu.DropdownMenuLinkItemProps, 'to'> {}

interface ItemOptionsViewProps extends DropdownMenu.DropdownMenuItemProps {}
interface ItemOptionsTogglePublishProps extends DropdownMenu.DropdownMenuItemProps {}
interface ItemExportQRCodeProps extends DropdownMenu.DropdownMenuItemProps {}
interface ItemOptionsDuplicateProps extends DropdownMenu.DropdownMenuItemProps {}
interface ItemOptionsCancelProps extends DropdownMenu.DropdownMenuItemProps {}
interface ItemOptionsEndListingProps extends DropdownMenu.DropdownMenuItemProps {}
interface ItemOptionsToggleHideListingProps extends DropdownMenu.DropdownMenuItemProps {}
interface ItemOptionsExtendProps extends DropdownMenu.DropdownMenuItemProps {}

interface ItemOptionsDeleteProps extends DropdownMenu.DropdownMenuItemProps {
    onDelete?: () => void;
}

const ItemOptions = (props: ItemOptionsProps) => {
    const { item, campaign, children, ...rest } = props;
    const routes = useCampaignItemRoutes();

    const contextValue = useMemo(() => ({ item, campaign, routes }), [item, campaign, routes]);

    return (
        <ItemOptionsContext.Provider value={contextValue}>
            <DropdownMenu.Root {...rest}>{children}</DropdownMenu.Root>
        </ItemOptionsContext.Provider>
    );
};

const ItemOptionsTogglePublish = (props: ItemOptionsTogglePublishProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { item, campaign } = useContext(ItemOptionsContext);

    const { mutate: togglePublish } = useMutation({
        mutationFn: (item: AuctionItem) =>
            updateCampaignItem({
                campaignId: campaign.id,
                itemId: item.id,
                data: { is_published: !item.is_published },
            }),

        onSuccess: data => {
            const nextMessage = data.is_published
                ? 'Item was published!'
                : 'Item was converted to a draft.';

            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.items(campaign.id) });
            toast.success(nextMessage);
        },
    });

    return (
        <DropdownMenu.Item
            disabled={!item.can_delete}
            onClick={() => togglePublish(item)}
            onSelect={e => e.preventDefault()}
            {...props}
        >
            <div className="flex gap-2">
                <div className="size-4">
                    <FontAwesomeIcon icon={faFilePen} />
                </div>
                <span>{item.is_published ? 'Save as draft' : 'List item'}</span>
            </div>
        </DropdownMenu.Item>
    );
};

const ItemOptionsEdit = (props: ItemOptionsEditProps) => {
    const { routes, item } = useContext(ItemOptionsContext);
    const isDisabled = didItemEnd(item);

    return (
        <div className={`${isDisabled ? 'cursor-not-allowed opacity-60' : ''}`}>
            <DropdownMenu.LinkItem
                to={routes.edit(item.id)}
                className={twJoin(isDisabled && 'pointer-events-none')}
                {...props}
            >
                <div className="flex gap-2">
                    <div className="size-4">
                        <FontAwesomeIcon icon={faPenToSquare} />
                    </div>
                    <span>Edit</span>
                </div>
            </DropdownMenu.LinkItem>
        </div>
    );
};

const ItemOptionsDuplicate = (props: ItemOptionsDuplicateProps) => {
    const { auctionsDuplicateItem = false } = useFlags();
    const { routes, item } = useContext(ItemOptionsContext);
    const history = useHistory();

    if (!auctionsDuplicateItem) {
        return null;
    }

    return (
        <DropdownMenu.Item
            {...props}
            className="flex-1"
            onSelect={e => {
                e.preventDefault();
                history.push(routes.duplicate(item.id));
            }}
        >
            <div className="flex gap-2">
                <div className="size-4">
                    <FontAwesomeIcon icon={faClone} />
                </div>
                <span>Duplicate</span>
            </div>
        </DropdownMenu.Item>
    );
};

const ItemOptionsViewItem = (props: ItemOptionsViewProps) => {
    const { campaign, item } = useContext(ItemOptionsContext);

    return (
        <UnstyledAnchor
            href={`${campaign.url}/auction/items/${item.id}`}
            target="_blank"
            rel="noreferrer"
        >
            <DropdownMenu.Item {...props} className="flex-1">
                <div className="flex gap-2">
                    <div className="size-4">
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </div>
                    <span>View item</span>
                </div>
            </DropdownMenu.Item>
        </UnstyledAnchor>
    );
};

const ItemOptionsViewDetails = (props: ItemOptionsDetailsProps) => {
    const { routes, item } = useContext(ItemOptionsContext);
    const { itemId } = useParams<CampaignItemRouteParams>();

    if (itemId) {
        return null;
    }

    return (
        <DropdownMenu.LinkItem to={routes.show(item.id)} {...props}>
            <div className="flex gap-2">
                <div className="size-4">
                    <FontAwesomeIcon icon={faEye} />
                </div>
                <span>Details</span>
            </div>
        </DropdownMenu.LinkItem>
    );
};

const ItemOptionsContent = DropdownMenu.Content;
const ItemOptionsTrigger = DropdownMenu.EllipsisTrigger;

const ItemOptionsCancelListing = (props: ItemOptionsCancelProps) => {
    const { item, campaign } = useContext(ItemOptionsContext);
    const isDisabled = !item.can_cancel;

    return (
        <CancelAuctionItem.Root campaignId={campaign.id} item={item}>
            <CancelAuctionItem.Trigger disabled={isDisabled}>
                <DropdownMenu.Item
                    {...props}
                    disabled={isDisabled}
                    onSelect={e => e.preventDefault()}
                >
                    <div className="flex gap-2">
                        <div className="size-4">
                            <FontAwesomeIcon icon={faBan} />
                        </div>
                        <span>Cancel listing</span>
                    </div>
                </DropdownMenu.Item>
            </CancelAuctionItem.Trigger>
        </CancelAuctionItem.Root>
    );
};

const ItemOptionsDelete = (props: ItemOptionsDeleteProps) => {
    const { onDelete, ...rest } = props;
    const { item, campaign } = useContext(ItemOptionsContext);
    const isDisabled = !item.can_delete;

    return (
        <DeleteItem.Root campaignId={campaign.id} item={item} onDelete={onDelete}>
            <DeleteItem.Trigger disabled={isDisabled}>
                <DropdownMenu.Item
                    {...rest}
                    disabled={isDisabled}
                    onSelect={e => e.preventDefault()}
                >
                    <div className="flex gap-2">
                        <div className="size-4">
                            <FontAwesomeIcon icon={faTrashXmark} />
                        </div>
                        <span>Delete</span>
                    </div>
                </DropdownMenu.Item>
            </DeleteItem.Trigger>
        </DeleteItem.Root>
    );
};

const ItemOptionsExportQRCode = (props: ItemExportQRCodeProps) => {
    const { item } = useContext(ItemOptionsContext);

    return (
        <ExportItemQRCode.Root item={item}>
            <ExportItemQRCode.Trigger>
                <DropdownMenu.Item {...props} onSelect={e => e.preventDefault()}>
                    <div className="flex gap-2">
                        <div className="size-4">
                            <FontAwesomeIcon icon={faQrcode} />
                        </div>
                        <span>QR code</span>
                    </div>
                </DropdownMenu.Item>
            </ExportItemQRCode.Trigger>
        </ExportItemQRCode.Root>
    );
};

const ItemOptionsEndListing = (props: ItemOptionsEndListingProps) => {
    const { item, campaign } = useContext(ItemOptionsContext);
    const isDisabled = !item.can_cancel;

    return (
        <EndListingItem.Root campaignId={campaign.id} item={item}>
            <EndListingItem.Trigger disabled={isDisabled}>
                <DropdownMenu.Item
                    {...props}
                    disabled={isDisabled}
                    onSelect={e => e.preventDefault()}
                >
                    <div className="flex gap-2">
                        <div className="size-4">
                            <FontAwesomeIcon icon={faCalendarXmark} />
                        </div>
                        <span>End listing</span>
                    </div>
                </DropdownMenu.Item>
            </EndListingItem.Trigger>
        </EndListingItem.Root>
    );
};

const ItemOptionsDeclareWinner = (props: ItemOptionsDeclareWinnerProps) => {
    const { routes, item } = useContext(ItemOptionsContext);
    const isSoldOut = item.dashboard_status === 'sold';
    const hasNoBidders = item.allows_bid && !item.price && !item.highest_bid;
    const isDisabled = hasNoBidders || isSoldOut;

    return (
        <div>
            <DropdownMenu.LinkItem
                to={routes.declareWinner(item.id)}
                {...props}
                disabled={isDisabled}
            >
                <div className="flex gap-2">
                    <div className="size-4">
                        <FontAwesomeIcon icon={faTrophy} />
                    </div>
                    <span>Declare winner</span>
                </div>
            </DropdownMenu.LinkItem>
        </div>
    );
};

const ItemOptionsToggleHideListing = (props: ItemOptionsToggleHideListingProps) => {
    const { item, campaign } = useContext(ItemOptionsContext);

    return (
        <ToggleHideListingItem.Root campaignId={campaign.id} item={item}>
            <ToggleHideListingItem.Trigger>
                <DropdownMenu.Item {...props} onSelect={e => e.preventDefault()}>
                    <div className="flex gap-2">
                        <div className="size-4">
                            <FontAwesomeIcon icon={faEyeSlash} />
                        </div>
                        <span>
                            {item.visibility === 'public' || item.visibility === 'featured'
                                ? 'Hide listing'
                                : 'Unhide listing'}
                        </span>
                    </div>
                </DropdownMenu.Item>
            </ToggleHideListingItem.Trigger>
        </ToggleHideListingItem.Root>
    );
};

const ItemOptionsPauseBidding = ({ onClick, ...props }: ItemOptionsEndListingProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { item, campaign } = useContext(ItemOptionsContext);

    const { mutate: pauseBidding } = useMutation({
        mutationFn: (item: AuctionItem) => pauseCampaignItemBidding(campaign.id, item.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.items(campaign.id) });
            toast.success('Bidding for an item is paused');
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message);
        },
    });

    if (item.dashboard_status !== 'live') {
        return null;
    }

    return (
        <DropdownMenu.Item
            {...props}
            onClick={e => {
                pauseBidding(item);
                onClick?.(e);
            }}
        >
            <div className="flex gap-2">
                <div className="size-4 pl-1">
                    <FontAwesomeIcon icon={faPause} />
                </div>
                <span>Pause bidding</span>
            </div>
        </DropdownMenu.Item>
    );
};

const ItemOptionsResumeBidding = ({ onClick, ...props }: ItemOptionsEndListingProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { item, campaign } = useContext(ItemOptionsContext);

    const { mutate: resumeBidding } = useMutation({
        mutationFn: (item: AuctionItem) => resumeCampaignItemBidding(campaign.id, item.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.items(campaign.id) });
            toast.success('Bidding for an item is resumed');
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message);
        },
    });

    if (item.dashboard_status !== 'paused') {
        return null;
    }

    return (
        <DropdownMenu.Item
            {...props}
            onClick={e => {
                resumeBidding(item);
                onClick?.(e);
            }}
        >
            <div className="flex gap-2">
                <div className="size-4 pl-1">
                    <FontAwesomeIcon icon={faPlay} />
                </div>
                <span>Resume bidding</span>
            </div>
        </DropdownMenu.Item>
    );
};

const ItemOptionsExtend = (props: ItemOptionsExtendProps) => {
    const { item } = useContext(ItemOptionsContext);

    if (!item.can_extend) {
        return null;
    }

    return (
        <Suspense fallback={<DropdownMenu.Item disabled={true}>Extend</DropdownMenu.Item>}>
            <ExtendItemDialog.Root>
                <ExtendItemDialog.Trigger asChild>
                    <DropdownMenu.Item onSelect={e => e.preventDefault()} {...props}>
                        <div className="flex gap-2">
                            <div className="size-4">
                                <FontAwesomeIcon icon={faCalendarCirclePlus} />
                            </div>
                            <span>Extend</span>
                        </div>
                    </DropdownMenu.Item>
                </ExtendItemDialog.Trigger>
            </ExtendItemDialog.Root>
        </Suspense>
    );
};

const Root = ItemOptions;
const Content = ItemOptionsContent;
const Trigger = ItemOptionsTrigger;
const TogglePublish = ItemOptionsTogglePublish;
const ViewItem = ItemOptionsViewItem;
const ViewDetails = ItemOptionsViewDetails;
const Edit = ItemOptionsEdit;
const Duplicate = ItemOptionsDuplicate;

const DeclareWinner = ItemOptionsDeclareWinner;
const CancelListing = ItemOptionsCancelListing;
const Delete = ItemOptionsDelete;
const ExportQRCode = ItemOptionsExportQRCode;
const EndListing = ItemOptionsEndListing;
const ToggleHideListing = ItemOptionsToggleHideListing;
const PauseBidding = ItemOptionsPauseBidding;
const ResumeBidding = ItemOptionsResumeBidding;
const Extend = ItemOptionsExtend;

export {
    ItemOptions,
    ItemOptionsContent,
    ItemOptionsTrigger,
    ItemOptionsTogglePublish,
    ItemOptionsViewItem,
    ItemOptionsViewDetails,
    ItemOptionsEdit,
    ItemOptionsDuplicate,
    ItemOptionsDeclareWinner,
    ItemOptionsCancelListing,
    ItemOptionsDelete,
    ItemOptionsExportQRCode,
    ItemOptionsEndListing,
    ItemOptionsToggleHideListing,
    ItemOptionsPauseBidding,
    ItemOptionsResumeBidding,
    ItemOptionsExtend,
    //
    Root,
    Content,
    Trigger,
    TogglePublish,
    ViewItem,
    DeclareWinner,
    CancelListing,
    ViewDetails,
    Edit,
    Duplicate,
    Delete,
    ExportQRCode,
    EndListing,
    ToggleHideListing,
    PauseBidding,
    ResumeBidding,
    Extend,
};
